/**
 * Leads (super admin only, same gate as AI B2B Leads): talk to Ask AI in plain English, review the matches, then pick some or
 * all of them and start a campaign. The campaign gets contacts only; nothing is called until it is started.
 *
 * Chat style: every question and its answer stays in the thread, conversations are kept in this browser and listed on the
 * left, and a follow-up is sent together with what the conversation already asked (see lib/leadsChat.ts), so "only the ones
 * with an email" means what it says. The matches of each question are saved on the server as a search job, so a turn only
 * remembers the job and its table is read back from there.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { keepPreviousData, useMutation, useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useLocation } from "wouter";
import { AlertTriangle, ListChecks, Loader2, MessageSquare, PanelLeft, Plus, RotateCcw, Search, SendHorizontal, Sparkles, Trash2, UserPlus, Users, X } from "lucide-react";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { CHAT_LIMITS, composeQuestion, loadChats, newId, saveChats, titleFor, type Chat, type ChatTurn } from "@/lib/leadsChat";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { AddToCampaignDialog } from "@/components/prospect-leads/AddToCampaignDialog";
import { ColumnPicker } from "@/components/prospect-leads/ColumnPicker";
import { useColumnChoice } from "@/components/prospect-leads/columns";
import { ExportButton } from "@/components/prospect-leads/ExportButton";
import { LeadsPager } from "@/components/prospect-leads/LeadsPager";
import { LeadsTable } from "@/components/prospect-leads/LeadsTable";
import type { ProspectLead, SearchResponse, SearchStatus, Vocabulary } from "@/components/prospect-leads/types";

const RESULTS_PER_PAGE = 50;
const FETCH_ALL_PAGE_SIZE = 100;   // the server's pageSize ceiling

const EXAMPLES = [
  "CTOs of software companies in the United States with a phone number",
  "Clinic owners in Sydney, Australia",
  "Marketing directors at e-commerce companies in the UK",
];

export default function LeadsSearchPage() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [, setLocation] = useLocation();

  const { data: user } = useQuery<{ isSuperAdmin?: boolean }>({ queryKey: ["/api/auth/me"] });
  const isSuperAdmin = user?.isSuperAdmin === true;
  const vocabQuery = useQuery<Vocabulary>({ queryKey: ["/api/prospect-leads/filter-vocabulary"], enabled: isSuperAdmin });
  const columns = useColumnChoice(vocabQuery.data);
  const statusQuery = useQuery<SearchStatus>({
    queryKey: ["prospect-leads", "search-status"],
    queryFn: async () => (await apiRequest("GET", "/api/prospect-leads/search/status")).json(),
    enabled: isSuperAdmin,
    staleTime: 0,
  });
  const status = statusQuery.data;
  const maxSelection = vocabQuery.data?.limits.maxSelection ?? 1000;

  // ---- conversations, kept in this browser ----
  const [chats, setChats] = useState<Chat[]>(() => loadChats());
  const [activeChatId, setActiveChatId] = useState<string | null>(() => loadChats()[0]?.id ?? null);
  const [activeTurnId, setActiveTurnId] = useState<string | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  useEffect(() => { saveChats(chats); }, [chats]);

  const chat = chats.find((c) => c.id === activeChatId) ?? null;
  const turns = chat?.turns ?? [];
  const shownTurn = turns.find((x) => x.id === activeTurnId) ?? [...turns].reverse().find((x) => !x.error && x.jobId) ?? null;
  const history = useMemo(() => [...chats].sort((a, b) => b.updatedAt - a.updatedAt), [chats]);

  const updateChat = (id: string, fn: (c: Chat) => Chat) => setChats((prev) => prev.map((c) => (c.id === id ? fn(c) : c)));

  // ---- the composer ----
  const [draft, setDraft] = useState("");
  const [maxResults, setMaxResults] = useState("");
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<Map<string, ProspectLead>>(new Map());
  const [showCampaign, setShowCampaign] = useState(false);
  const [phoneOnly, setPhoneOnly] = useState(false);

  const ask = useMutation({
    mutationFn: async (v: { chatId: string; text: string; prior: ChatTurn[] }) => {
      const n = Number.parseInt(maxResults, 10);
      const res = await apiRequest("POST", "/api/prospect-leads/ask", { question: composeQuestion(v.prior, v.text), ...(Number.isFinite(n) && n > 0 ? { maxResults: n } : {}) });
      return (await res.json()) as { jobId: string; explanation: string | null; truncated: boolean };
    },
    onSuccess: (r, v) => {
      const turn: ChatTurn = { id: newId(), question: v.text, at: Date.now(), jobId: r.jobId, explanation: r.explanation, truncated: r.truncated, count: null, error: null };
      updateChat(v.chatId, (c) => ({ ...c, updatedAt: turn.at, turns: [...c.turns, turn].slice(-CHAT_LIMITS.turnsPerChat) }));
      setActiveTurnId(turn.id); setPage(1); setSelected(new Map());
    },
    // The reason stays in the thread, with the question it belongs to, instead of a toast that is gone before it can be read.
    onError: (e: any, v) => {
      const turn: ChatTurn = { id: newId(), question: v.text, at: Date.now(), jobId: "", explanation: null, truncated: false, count: null, error: e?.message || t("leadsSearch.failed", "The search failed") };
      updateChat(v.chatId, (c) => ({ ...c, updatedAt: turn.at, turns: [...c.turns, turn].slice(-CHAT_LIMITS.turnsPerChat) }));
    },
  });

  const unavailable = status && (!status.available || status.mode !== "leadpro");
  const canSend = draft.trim().length >= 3 && status?.available === true && status.mode === "leadpro" && !ask.isPending;

  const send = () => {
    if (!canSend) return;
    const text = draft.trim();
    let id = chat?.id ?? null;
    if (!id) {
      id = newId();
      const created: Chat = { id, title: titleFor(text), updatedAt: Date.now(), turns: [] };
      setChats((prev) => [created, ...prev].slice(0, CHAT_LIMITS.chats));
      setActiveChatId(id);
    }
    ask.mutate({ chatId: id, text, prior: chat?.turns ?? [] });
    setDraft("");
  };

  const newChat = () => { setActiveChatId(null); setActiveTurnId(null); setSelected(new Map()); setPage(1); setDraft(""); setShowHistory(false); };
  const openChat = (id: string) => { setActiveChatId(id); setActiveTurnId(null); setSelected(new Map()); setPage(1); setShowHistory(false); };
  const deleteChat = (id: string) => {
    setChats((prev) => prev.filter((c) => c.id !== id));
    if (id === activeChatId) newChat();
  };
  const showResults = (turn: ChatTurn) => { setActiveTurnId(turn.id); setPage(1); setSelected(new Map()); };

  // ---- the results of the turn on show ----
  const jobId = shownTurn?.jobId || null;
  const jobQuery = useQuery<SearchResponse>({
    queryKey: ["prospect-leads", "search", jobId, page, phoneOnly],
    queryFn: async () => (await apiRequest("GET", `/api/prospect-leads/search/${jobId}?page=${page}&pageSize=${RESULTS_PER_PAGE}${phoneOnly ? "&hasPhone=1" : ""}`)).json(),
    enabled: !!jobId && isSuperAdmin,
    placeholderData: keepPreviousData,
    staleTime: 0,
    retry: false,
  });
  const rows = jobQuery.data?.results.data ?? [];
  const total = jobQuery.data?.results.pagination.totalItems ?? 0;

  // Remember how many leads a search found, so the thread can say so for turns whose table is not open.
  useEffect(() => {
    if (!chat || !shownTurn || !jobQuery.data || shownTurn.count === total) return;
    updateChat(chat.id, (c) => ({ ...c, turns: c.turns.map((x) => (x.id === shownTurn.id ? { ...x, count: total } : x)) }));
  }, [jobQuery.data, total]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep the newest message in view.
  const bottomRef = useRef<HTMLDivElement>(null);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" }); }, [turns.length, ask.isPending]);

  // ---- selection: some rows, a page, or every match (read from the server page by page, up to the selection cap) ----
  const withinCap = (n: number) => { if (n <= maxSelection) return true; toast({ title: t("prospectLeads.leads.selectionCap", { max: maxSelection }), variant: "destructive" }); return false; };
  const toggleRow = (lead: ProspectLead) => {
    const next = new Map(selected);
    if (next.has(lead.id)) next.delete(lead.id); else { if (!withinCap(next.size + 1)) return; next.set(lead.id, lead); }
    setSelected(next);
  };
  const togglePage = () => {
    const next = new Map(selected);
    if (rows.length > 0 && rows.every((r) => next.has(r.id))) rows.forEach((r) => next.delete(r.id));
    else { rows.forEach((r) => next.set(r.id, r)); if (!withinCap(next.size)) return; }
    setSelected(next);
  };
  const selectAll = useMutation({
    mutationFn: async () => {
      const all: ProspectLead[] = [];
      for (let p = 1; all.length < Math.min(total, maxSelection); p++) {
        const res = (await (await apiRequest("GET", `/api/prospect-leads/search/${jobId}?page=${p}&pageSize=${FETCH_ALL_PAGE_SIZE}${phoneOnly ? "&hasPhone=1" : ""}`)).json()) as SearchResponse;
        all.push(...res.results.data);
        if (p >= res.results.pagination.totalPages) break;
      }
      return all.slice(0, maxSelection);
    },
    onSuccess: (all) => {
      setSelected(new Map(all.map((l) => [l.id, l] as const)));
      if (all.length < total) toast({ title: t("prospectLeads.leads.selectionCap", { max: maxSelection }) });
    },
    onError: (e: any) => toast({ title: t("leadsSearch.selectAllFailed", "Could not select all the results"), description: e?.message, variant: "destructive" }),
  });
  const selectedList = useMemo(() => [...selected.values()], [selected]);
  const allSelected = total > 0 && selected.size >= Math.min(total, maxSelection);

  // The API answers anyone else with 403; send them home rather than show a page that cannot work.
  useEffect(() => {
    if (user && !isSuperAdmin) setLocation("/app", { replace: true });
  }, [user, isSuperAdmin, setLocation]);
  if (!isSuperAdmin) return null;

  const resultsPanel = (
    <Card data-testid="leads-search-results">
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className="text-base">{t("prospectLeads.search.resultsTitle")}{jobQuery.data ? ` (${total})` : ""}</CardTitle>
          <div className="flex items-center gap-2">
            {jobQuery.data && (
              <label className="flex cursor-pointer items-center gap-2 text-sm" data-testid="leads-phone-only">
                <input type="checkbox" checked={phoneOnly} onChange={(e) => { setPhoneOnly(e.target.checked); setPage(1); setSelected(new Map()); }} />
                {t("leadsSearch.phoneOnly", "With phone number only")}
              </label>
            )}
            {total > 0 && <ColumnPicker choice={columns} groups={vocabQuery.data?.columnGroups ?? []} />}
            {total > 0 && jobId && <ExportButton request={{ scope: "search", jobId, columns: columns.keys }} label={t("prospectLeads.export.button")} testId="leads-export-results" />}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {jobQuery.isError ? (
          <p className="text-sm text-muted-foreground">{t("leadsSearch.resultsGone", "These results are no longer available. Ask the question again to get them back.")}</p>
        ) : (
          <>
            {total > 0 && (
              <div className="flex flex-wrap items-center gap-2 rounded-lg border bg-muted/40 p-3" data-testid="leads-selection-bar">
                <span className="text-sm font-medium">{t("prospectLeads.leads.selected", { n: selected.size })}</span>
                {!allSelected && (
                  <Button size="sm" variant="outline" onClick={() => selectAll.mutate()} disabled={selectAll.isPending} data-testid="leads-select-all">
                    {selectAll.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ListChecks className="mr-2 h-4 w-4" />}
                    {t("leadsSearch.selectAll", { count: Math.min(total, maxSelection), defaultValue: "Select all {{count}}" })}
                  </Button>
                )}
                <Button size="sm" onClick={() => setShowCampaign(true)} disabled={selected.size === 0} data-testid="leads-add-to-campaign">
                  <UserPlus className="mr-2 h-4 w-4" />{t("leadsSearch.createCampaign", "Create campaign")}
                </Button>
                {selected.size > 0 && <ExportButton request={{ scope: "ids", ids: [...selected.keys()], columns: columns.keys }} label={t("prospectLeads.export.selected", { count: selected.size })} testId="leads-export-selected" />}
                {selected.size > 0 && <Button size="sm" variant="ghost" onClick={() => setSelected(new Map())}><X className="mr-2 h-4 w-4" />{t("prospectLeads.leads.clearSelection")}</Button>}
              </div>
            )}
            <LeadsTable rows={rows} columns={columns.selected} loading={jobQuery.isLoading} selected={selected} onToggleRow={toggleRow} onTogglePage={togglePage} emptyText={t("prospectLeads.search.none")} />
            {jobQuery.data && <LeadsPager page={page} pageSize={RESULTS_PER_PAGE} totalItems={total} onPage={setPage} />}
          </>
        )}
      </CardContent>
    </Card>
  );

  const historyList = (
    <div className="flex h-full flex-col gap-2" data-testid="leads-chat-history">
      <Button variant="outline" className="w-full justify-start" onClick={newChat} data-testid="leads-new-chat"><Plus className="mr-2 h-4 w-4" />{t("leadsSearch.newChat", "New chat")}</Button>
      <ul className="space-y-1 overflow-y-auto">
        {history.length === 0 && <li className="px-2 py-1 text-xs text-muted-foreground">{t("leadsSearch.noChats", "Your searches will be kept here.")}</li>}
        {history.map((c) => (
          <li key={c.id} className={`group flex items-center gap-1 rounded-md ${c.id === activeChatId ? "bg-muted" : "hover:bg-muted/50"}`}>
            <button type="button" className="flex min-w-0 flex-1 items-center gap-2 px-2 py-2 text-left text-sm" onClick={() => openChat(c.id)}>
              <MessageSquare className="h-4 w-4 shrink-0 text-muted-foreground" /><span className="truncate">{c.title}</span>
            </button>
            <button type="button" className="mr-1 rounded p-1 text-muted-foreground opacity-0 hover:text-destructive focus:opacity-100 group-hover:opacity-100" onClick={() => deleteChat(c.id)} aria-label={t("leadsSearch.deleteChat", "Delete chat")}>
              <Trash2 className="h-4 w-4" />
            </button>
          </li>
        ))}
      </ul>
    </div>
  );

  return (
    <div className="space-y-4" data-testid="leads-search-page">
      <div className="flex items-center gap-4">
        <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-violet-500 to-indigo-600 text-white shadow">
          <Users className="h-6 w-6" />
        </div>
        <div className="min-w-0 flex-1">
          <h1 className="text-2xl font-bold tracking-tight">{t("leadsSearch.title", "Leads")}</h1>
          <p className="text-sm text-muted-foreground">{t("leadsSearch.subtitle", "Describe who you want to reach, then add some or all of the matches to a campaign.")}</p>
        </div>
        <Button variant="outline" size="icon" className="md:hidden" onClick={() => setShowHistory((v) => !v)} aria-label={t("leadsSearch.history", "Search history")}><PanelLeft className="h-4 w-4" /></Button>
      </div>

      {unavailable && (
        <Alert variant="destructive" data-testid="leads-search-unavailable">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>{t("prospectLeads.search.unavailable")}</AlertTitle>
          <AlertDescription>{status.message ?? t("leadsSearch.notLeadPro", "Plain-English search is not set up on this server.")}</AlertDescription>
        </Alert>
      )}

      <div className="flex items-start gap-6">
        <aside className="hidden w-64 shrink-0 md:block">{historyList}</aside>
        {showHistory && <aside className="w-full md:hidden">{historyList}</aside>}

        <section className="flex min-w-0 flex-1 flex-col gap-5">
          {turns.length === 0 && !ask.isPending && (
            <div className="rounded-xl border border-dashed p-6 text-center" data-testid="leads-chat-empty">
              <Search className="mx-auto mb-2 h-6 w-6 text-muted-foreground" />
              <p className="text-sm font-medium">{t("leadsSearch.askTitle", "Ask AI")}</p>
              <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">{t("leadsSearch.askDescription", "Write it the way you would say it. Follow-up questions build on this chat, so you can narrow a search step by step.")}</p>
              <div className="mt-3 flex flex-wrap justify-center gap-2">
                {EXAMPLES.map((ex) => (
                  <button key={ex} type="button" className="rounded-full border px-3 py-1 text-xs text-muted-foreground hover:bg-muted/50" onClick={() => setDraft(ex)}>{ex}</button>
                ))}
              </div>
            </div>
          )}

          {turns.map((turn) => (
            <div key={turn.id} className="space-y-3" data-testid="leads-chat-turn">
              <div className="flex justify-end">
                <div className="max-w-[85%] whitespace-pre-wrap rounded-2xl bg-primary px-4 py-2 text-sm text-primary-foreground">{turn.question}</div>
              </div>
              <div className="flex gap-3">
                <div className="mt-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-violet-100 text-violet-700 dark:bg-violet-950 dark:text-violet-300"><Sparkles className="h-4 w-4" /></div>
                <div className="min-w-0 flex-1 space-y-3">
                  {turn.error ? (
                    <Alert variant="destructive" data-testid="leads-search-error">
                      <AlertTriangle className="h-4 w-4" />
                      <AlertTitle>{t("leadsSearch.failed", "The search failed")}</AlertTitle>
                      <AlertDescription>
                        {turn.error}
                        <span className="mt-1 block">{t("leadsSearch.tryNarrower", "A narrower question usually works: one role and one place, for example \"CEOs of clinics in Mumbai with a phone number\", or fewer leads.")}</span>
                        <Button size="sm" variant="outline" className="mt-2" onClick={() => setDraft(turn.question)}><RotateCcw className="mr-2 h-3.5 w-3.5" />{t("leadsSearch.editAndRetry", "Edit and try again")}</Button>
                      </AlertDescription>
                    </Alert>
                  ) : (
                    <>
                      {turn.explanation && <p className="text-sm" data-testid="leads-search-explanation"><span className="font-medium">{t("prospectLeads.find.explanation")}: </span>{turn.explanation}</p>}
                      {turn.truncated && <p className="text-sm text-muted-foreground">{t("prospectLeads.ask.truncated", "More leads match than are shown. Narrow the description to see others.")}</p>}
                      {shownTurn?.id === turn.id ? resultsPanel : (
                        <Button size="sm" variant="outline" onClick={() => showResults(turn)}>
                          {turn.count != null ? t("leadsSearch.showResultsCount", { count: turn.count, defaultValue: "Show {{count}} results" }) : t("leadsSearch.showResults", "Show results")}
                        </Button>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}

          {ask.isPending && ask.variables && ask.variables.chatId === (chat?.id ?? null) && (
            <div className="space-y-3" data-testid="leads-chat-pending">
              <div className="flex justify-end"><div className="max-w-[85%] whitespace-pre-wrap rounded-2xl bg-primary px-4 py-2 text-sm text-primary-foreground">{ask.variables.text}</div></div>
              <div className="flex items-center gap-3 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />{t("prospectLeads.ask.searching", "Searching...")} {t("prospectLeads.ask.wait", "This can take up to a minute.")}
              </div>
            </div>
          )}
          <div ref={bottomRef} />

          <div className="sticky bottom-0 -mx-1 space-y-2 border-t bg-background px-1 pb-3 pt-3">
            {turns.length > 0 && <p className="text-xs text-muted-foreground">{t("leadsSearch.followUpHint", "Follow-ups build on this chat. Start a new chat for a different search.")}</p>}
            <div className="flex items-end gap-2">
              <Textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) { e.preventDefault(); send(); } }}
                placeholder={turns.length > 0 ? t("leadsSearch.followUpPlaceholder", "Refine it, e.g. only those with an email") : t("prospectLeads.ask.placeholder", "Describe the leads you want, e.g. CTOs of software companies in the United States with a phone number")}
                maxLength={CHAT_LIMITS.question}
                rows={2}
                className="min-h-[3rem] flex-1 resize-none"
                data-testid="leads-search-question"
              />
              <div className="space-y-1">
                <label htmlFor="leads-max-results" className="block text-xs text-muted-foreground">{t("prospectLeads.search.howMany")}</label>
                <Input id="leads-max-results" type="number" min={1} max={status?.limits.maxResults ?? 500} value={maxResults} onChange={(e) => setMaxResults(e.target.value)} placeholder={String(status?.limits.defaultMaxResults ?? 100)} className="w-24" />
              </div>
              <Button onClick={send} disabled={!canSend} data-testid="leads-search-submit" aria-label={t("prospectLeads.ask.search", "Search")}>
                {ask.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <SendHorizontal className="h-4 w-4" />}
              </Button>
            </div>
          </div>
        </section>
      </div>

      <AddToCampaignDialog
        open={showCampaign}
        onOpenChange={setShowCampaign}
        leads={selectedList}
        newOnly
        onDone={() => { setSelected(new Map()); queryClient.invalidateQueries({ queryKey: ["prospect-leads", "search"] }); }}
      />
    </div>
  );
}
